import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { notasEnvioApi } from '../api/remitos'
import { fmtFecha, fmtFechaHora } from '../utils/fecha'
import { etiquetaCliente } from '../utils/cliente'
import EstadoVigenteBadge from '../components/EstadoVigenteBadge'
import Boton from '../components/Boton'
import AndreaniHistorial from '../components/AndreaniHistorial'
import api from '../api/client'
import { colors, font, text, space, radius, sx } from '../design/tokens'

/**
 * Detalle de una Nota de Envío (rediseño 31 ago 2026, pedido de Martin).
 * Patrón del detalle de remito: toolbar sticky + documento como ancla visual
 * + columna lateral con el viaje y la traza. El estado de despacho que se
 * muestra es SIEMPRE `estado_vigente` (la única fuente de verdad — jamás el
 * escalar crudo); el escalar solo aparece en el historial, que es su lugar.
 */

// Estados del CICLO DE VIDA (escalar) — solo para pintar el historial.
const ESTADOS = {
  procesado:       { bg: colors.infoBg, color: colors.info, label: 'Procesado' },
  en_distribucion: { bg: colors.t80Bg, color: colors.t80Text, label: 'En distribución' },
  entregado:       { bg: colors.okBg, color: colors.ok, label: 'Entregado' },
  no_entregado:    { bg: colors.errBg, color: colors.err, label: 'No entregado' },
  reagendado:      { bg: colors.warnBg, color: colors.warn, label: 'Reagendado' },
}

function ChipEscalar({ estado }) {
  const cfg = ESTADOS[estado] || { bg: colors.bgSoft, color: colors.textSec, label: estado }
  return <span style={{ ...sx.badge, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
}

// Resultado de la parada en UN viaje (dato del backend, no se deriva nada).
function resultadoViaje(sd) {
  const r = {
    entregado:    { bg: colors.okBg, color: colors.ok, label: '✓ Entregada' },
    no_entregado: { bg: colors.errBg, color: colors.err, label: '✕ No entregada' },
    reagendado:   { bg: colors.warnBg, color: colors.warn, label: '↻ Reagendada' },
  }[sd.estado_entrega]
  if (r) return r
  if (sd.salida?.estado === 'despachada') return { bg: colors.infoBg, color: colors.info, label: '🚚 En reparto' }
  if (sd.salida?.estado === 'armando') return { bg: colors.warnBg, color: colors.warn, label: 'En armado' }
  return { bg: colors.bgSoft, color: colors.textSec, label: '—' }
}

const hhmm = (h) => (h ? String(h).slice(0, 5) : null)

export default function NotaEnvioDetallePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [ne, setNe] = useState(null)
  const [loading, setLoading] = useState(true)
  const [imprimiendo, setImprimiendo] = useState(false)
  const [reimprimiendo, setReimprimiendo] = useState(false)
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')
  // Caso de entrega: la NE se entregó pero llegó con roturas / faltantes /
  // mal estado. Relato + selección → reposición T80 al mismo obsequiado.
  const [showCaso, setShowCaso] = useState(false)
  const [casoDetalle, setCasoDetalle] = useState('')
  const [casoSel, setCasoSel] = useState({}) // articulo_id → cantidad a reponer
  const [casoEnviando, setCasoEnviando] = useState(false)
  const [casoError, setCasoError] = useState('')

  const cargar = async () => {
    setLoading(true)
    try {
      const { data } = await notasEnvioApi.obtener(id)
      setNe(data)
    } catch {
      navigate('/remitos')
    } finally { setLoading(false) }
  }

  useEffect(() => { cargar() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleImprimir = async () => {
    setImprimiendo(true)
    setMsg(''); setError('')
    try {
      await notasEnvioApi.imprimir(id)
      setMsg('Impresión registrada correctamente.')
      cargar()
    } catch (err) {
      setError(err.response?.data?.message || 'Error al registrar impresión.')
    } finally { setImprimiendo(false) }
  }

  // Reimpresión inmediata con motivo obligatorio (mismo criterio que el remito
  // T90): huella en impresiones + historial, y el PDF se abre al instante.
  const handleReimprimir = async () => {
    const motivo = window.prompt('Motivo de la reimpresión (queda registrado):')
    if (motivo === null) return
    if (motivo.trim().length < 3) { setError('El motivo es obligatorio (mínimo 3 caracteres).'); return }
    setReimprimiendo(true)
    setMsg(''); setError('')
    try {
      await notasEnvioApi.reimprimir(id, motivo.trim())
      setMsg('Reimpresión registrada.')
      await handlePdf()
      cargar()
    } catch (err) {
      setError(err.response?.data?.message || 'Error al reimprimir.')
    } finally { setReimprimiendo(false) }
  }

  const handleAbrirCaso = async () => {
    if (!casoDetalle.trim()) { setCasoError('Contá qué pasó con la entrega.'); return }
    const items = Object.entries(casoSel)
      .map(([aid, cant]) => ({ articulo_id: parseInt(aid, 10), cantidad: parseInt(cant, 10) || 0 }))
      .filter(i => i.cantidad > 0)
    setCasoEnviando(true); setCasoError('')
    try {
      const { data } = await api.post(`/notas-envio/${id}/abrir-caso`, { detalle: casoDetalle.trim(), items })
      const repoNe = data.caso?.reposicion_nota_envio
      setMsg(data.advertencia
        ? `Caso #${data.caso.id} abierto — ⚠ ${data.advertencia}`
        : (repoNe
          ? `Caso #${data.caso.id} abierto. Reposición generada: NE nueva al mismo destinatario, ya disponible en Asignaciones (el remito fiscal sale al cliente original).`
          : `Caso #${data.caso.id} abierto (sin reposición).`))
      setShowCaso(false); setCasoDetalle(''); setCasoSel({})
      await cargar()
    } catch (err) {
      setCasoError(err.response?.data?.message || 'No se pudo abrir el caso.')
    } finally { setCasoEnviando(false) }
  }

  const handlePdf = async () => {
    try {
      const { data } = await notasEnvioApi.pdf(id)
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo generar el PDF.')
    }
  }

  if (loading) return <p style={{ color: colors.textSec, fontSize: text.body }}>Cargando nota de envío…</p>
  if (!ne) return null

  const dest = ne.destinatario || {}
  const direccionPartes = [
    dest.calle,
    [dest.piso && `Piso ${dest.piso}`, dest.depto && `Depto ${dest.depto}`].filter(Boolean).join(' '),
    dest.localidad,
    dest.provincia,
    dest.codigo_postal,
  ].filter(Boolean)
  const yaImpresa = (ne.impresiones_count || 0) > 0
  // Viajes de esta NE (dato del backend: salida + fletero + resultado de la
  // parada). El más nuevo arriba. El estado que manda es ne.estado_vigente.
  const viajes = [...(ne.salida_documentos || [])].sort((a, b) => b.id - a.id)
  const totalUnidades = (ne.articulos || []).reduce((acc, a) => acc + Number(a.cantidad || 0), 0)

  return (
    <div style={st.wrap}>
      {/* Toolbar sticky: breadcrumb + estado + acciones siempre visibles */}
      <div style={sx.toolbar}>
        <Boton variante="ghost" sm onClick={() => navigate(-1)} title="Volver">←</Boton>
        <div style={st.breadcrumb}>
          <span>Notas de envío</span>
          <span style={{ color: colors.textPlh }}>›</span>
          <code style={st.breadcrumbNum}>{ne.numero_ne}</code>
        </div>
        <span style={{ ...sx.badge, background: colors.t80Bg, color: colors.t80Text }}>NE</span>
        <EstadoVigenteBadge estadoVigente={ne.estado_vigente} />
        {yaImpresa && <span style={{ ...sx.badge, background: colors.okBg, color: colors.ok }}>Impresa ×{ne.impresiones_count}</span>}
        <div style={{ flex: 1 }} />
        <div style={st.toolbarAcciones}>
          {ne.token_seguimiento && dest.telefono && (
            <Boton variante="send" sm type="button" title={`Avisarle a ${dest.nombre || 'el destinatario'} por WhatsApp (${dest.telefono})`}
              onClick={() => {
                const url = `${window.location.origin}/seguimiento/${ne.token_seguimiento}`
                const texto = `¡Hola${dest.nombre ? ` ${dest.nombre}` : ''}! Tenés un envío de Vinoteca Ligier en camino 🍷 Podés seguirlo acá: ${url}`
                const tel = dest.telefono.replace(/\D/g, '')
                window.open(`https://wa.me/${tel}?text=${encodeURIComponent(texto)}`, '_blank')
              }}>
              📱 WhatsApp
            </Boton>
          )}
          {ne.token_seguimiento && (
            <Boton variante="secondary" sm type="button" title="Copiar link de seguimiento para el destinatario"
              onClick={async () => {
                await navigator.clipboard.writeText(`${window.location.origin}/seguimiento/${ne.token_seguimiento}`)
                setMsg('Link de seguimiento copiado — pasáselo al destinatario por WhatsApp o mail.')
              }}>
              🔗 Seguimiento
            </Boton>
          )}
          {ne.estado === 'entregado' && (
            <Boton variante="secondary" sm type="button" onClick={() => { setShowCaso(v => !v); setCasoError('') }}
              title="Rotura, faltante o mercadería en mal estado: registrar qué pasó y generar la reposición al mismo destinatario">
              ⚠️ Abrir caso
            </Boton>
          )}
          {/* Atajo canónico al próximo paso (patrón del remito T90): armar la
              salida con esta NE ya preseleccionada. primary solo cuando el
              slot está libre (ya impresa → "Imprimir" mutó a secondary). */}
          {ne.estado_vigente?.accionable && (
            <Boton variante={yaImpresa ? 'primary' : 'secondary'} sm type="button"
              onClick={() => navigate('/salidas/nueva', { state: { preseleccion: [{
                entidad_tipo: 'nota_envio',
                entidad_id: ne.id,
                label: dest.nombre || ne.numero_ne,
                sub: ne.numero_ne,
                calle: dest.calle || '',
                localidad: dest.localidad || '',
                cp: dest.codigo_postal || '',
                zona: ne.zona?.nombre || '',
                zona_id: ne.zona?.id || ne.zona_id || null,
              }] } })}
              title="Armar una salida con esta NE preseleccionada">
              🚚 Asignar a salida
            </Boton>
          )}
          <Boton variante="secondary" sm onClick={handlePdf} type="button">📄 PDF</Boton>
          {!yaImpresa
            ? <Boton variante="primary" sm disabled={imprimiendo} onClick={handleImprimir} type="button">{imprimiendo ? 'Registrando…' : '🖨 Imprimir'}</Boton>
            : <Boton variante="secondary" sm disabled={reimprimiendo} onClick={handleReimprimir} type="button">{reimprimiendo ? 'Solicitando…' : 'Reimprimir'}</Boton>}
        </div>
      </div>

      {msg && <div style={st.successBox}>{msg}</div>}
      {error && <div style={st.errorBox}>{error}</div>}

      {/* Caso de entrega (T80): relato + productos a reponer. La reposición
          va como T80 nuevo con NE al MISMO destinatario; la NV solo-remito
          (sin factura ni recibo) sale al cliente original. */}
      {showCaso && (
        <div style={st.casoBox}>
          <strong style={{ fontSize: text.body }}>Abrir caso — ¿qué pasó con la entrega a {dest.nombre || 'este destinatario'}?</strong>
          <textarea
            style={st.casoTextarea}
            value={casoDetalle}
            onChange={e => setCasoDetalle(e.target.value)}
            placeholder="Ej: llegó una botella rota y faltó el estuche"
            autoFocus
          />
          <div style={{ margin: '10px 0 6px', fontSize: text.sec, color: colors.textSub }}>
            Productos a reponer (opcional): se genera un T80 nuevo con una NE al <strong>mismo destinatario</strong>,
            sin factura ni recibo, que vuelve a <strong>Asignaciones</strong> para una nueva entrega.
          </div>
          {(ne.articulos || []).map(a => {
            const art = a.articulo?.factura_como || a.articulo
            const sel = casoSel[a.articulo_id] !== undefined
            return (
              <label key={a.id} style={st.casoItem}>
                <input
                  type="checkbox"
                  checked={sel}
                  onChange={() => setCasoSel(prev => {
                    const next = { ...prev }
                    if (sel) delete next[a.articulo_id]
                    else next[a.articulo_id] = a.cantidad
                    return next
                  })}
                />
                <code style={{ ...sx.mono, fontSize: text.sec, color: colors.textSec }}>{art?.codigo_alfa}</code>
                <span style={{ flex: 1 }}>{art?.descripcion}</span>
                {sel ? (
                  <input
                    type="number" min="1" max={a.cantidad}
                    style={{ ...sx.input, width: 64, minWidth: 64, textAlign: 'right' }}
                    value={casoSel[a.articulo_id]}
                    onClick={e => e.preventDefault()}
                    onChange={e => setCasoSel(prev => ({ ...prev, [a.articulo_id]: e.target.value }))}
                  />
                ) : (
                  <code style={{ ...sx.mono, fontSize: text.sec, color: colors.textMeta }}>×{a.cantidad}</code>
                )}
              </label>
            )
          })}
          <div style={{ display: 'flex', gap: space.md, marginTop: space.lg }}>
            <Boton variante="primary" disabled={casoEnviando} onClick={handleAbrirCaso} type="button">
              {casoEnviando ? 'Abriendo…' : 'Abrir caso'}
            </Boton>
            <Boton variante="ghost" type="button" onClick={() => { setShowCaso(false); setCasoError('') }}>Cancelar</Boton>
          </div>
          {casoError && <div style={{ color: colors.err, fontSize: text.sec, marginTop: space.sm }}>{casoError}</div>}
        </div>
      )}

      <div style={st.grid}>
        {/* ── Columna principal: el documento ─────────────────────────── */}
        <div style={st.mainCol}>
          <div style={st.documento}>
            <div style={st.docHeader}>
              <div style={st.docHeaderLeft}>
                <span style={st.docLabel}>Nota de envío</span>
                <span style={st.docNumero}>{ne.numero_ne}</span>
              </div>
              {ne.remito && (
                <div style={{ textAlign: 'right' }}>
                  <span style={st.docLabel}>Remito padre</span>
                  <div>
                    <button style={st.linkDoc} onClick={() => navigate(`/remitos/${ne.remito.id}`)} title="Ver el remito T80 padre">
                      {ne.remito.numero_presea} →
                    </button>
                  </div>
                  <div style={{ fontSize: text.sec, color: colors.textSec, marginTop: 2 }}>{etiquetaCliente(ne.remito.cliente)}</div>
                </div>
              )}
            </div>

            {/* Destinatario: a quién le toca el timbre el repartidor */}
            <div style={st.docDestinatario}>
              <div style={st.docDestNombre}>{dest.nombre || <span style={sx.placeholder}>Sin destinatario</span>}</div>
              <div style={st.docDestDatos}>
                <span>{direccionPartes.length > 0 ? direccionPartes.join(', ') : <span style={sx.placeholder}>Sin dirección</span>}</span>
                {dest.telefono && (
                  <>
                    <span style={{ color: colors.textPlh }}>·</span>
                    <span style={sx.mono}>{dest.telefono}</span>
                  </>
                )}
              </div>
              {dest.notas && (
                <div style={st.docDestNotas} title="Notas para la entrega">📝 {dest.notas}</div>
              )}
            </div>

            {/* Artículos a entregar */}
            {/* Tabla estilo "papel" (docTh sin fondo, línea negra bajo el
                header) — el mismo lenguaje que el documento del remito. */}
            <div style={st.docDetalle}>
              {(!ne.articulos || ne.articulos.length === 0) ? (
                <p style={{ ...sx.placeholder, fontSize: text.body, margin: 0 }}>Sin artículos cargados.</p>
              ) : (
                <table style={st.docTable}>
                  <thead>
                    <tr>
                      <th style={st.docTh}>Código</th>
                      <th style={st.docTh}>Descripción</th>
                      <th style={{ ...st.docTh, textAlign: 'right' }}>Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ne.articulos.map(a => {
                      const art = a.articulo?.factura_como || a.articulo
                      return (
                        <tr key={a.id} style={st.docTr}>
                          <td style={st.docTdCodigo}>{art?.codigo_alfa}</td>
                          <td style={st.docTd}>{art?.descripcion}</td>
                          <td style={{ ...st.docTd, ...sx.mono, textAlign: 'right', fontWeight: 600 }}>{a.cantidad}</td>
                        </tr>
                      )
                    })}
                    <tr>
                      <td colSpan={2} style={{ ...st.docTd, fontWeight: 600, textAlign: 'right', color: colors.textSec, fontSize: text.sec }}>Total unidades</td>
                      <td style={{ ...st.docTd, ...sx.mono, fontWeight: 700, textAlign: 'right' }}>{totalUnidades}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Casos de entrega de esta NE + su reposición */}
          {ne.casos?.length > 0 && (
            <div style={sx.card}>
              <h3 style={{ ...sx.cardTitle, marginBottom: space.lg }}>⚠️ Casos de entrega ({ne.casos.length})</h3>
              {ne.casos.map(caso => (
                <div key={caso.id} style={st.casoFila}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: text.body }}>Caso #{caso.id}</strong>
                    <span style={{
                      ...sx.badge,
                      background: caso.estado === 'abierto' ? colors.warnBg : colors.okBg,
                      color: caso.estado === 'abierto' ? colors.warn : colors.ok,
                    }}>{caso.estado}</span>
                    <span style={{ color: colors.textSec, fontSize: text.sec }}>
                      {caso.usuario?.nombre} · <span style={sx.mono}>{fmtFechaHora(caso.created_at)}</span>
                    </span>
                  </div>
                  <div style={{ marginTop: 4, color: colors.textSub, fontSize: text.body }}>{caso.detalle}</div>
                  {caso.items?.length > 0 && (
                    <div style={{ marginTop: 4, color: colors.textSec, fontSize: text.sec }}>
                      Repone: {caso.items.map(i => `${i.cantidad}× ${i.codigo || i.descripcion}`).join(' · ')}
                    </div>
                  )}
                  {(caso.reposicion_nota_venta || caso.reposicion_nota_envio || caso.reposicion_remito) && (
                    <div style={{ marginTop: 6, display: 'flex', gap: space.lg, flexWrap: 'wrap', fontSize: text.sec }}>
                      {caso.reposicion_nota_venta && (
                        <button style={st.linkDoc} onClick={() => navigate(`/notas-venta/${caso.reposicion_nota_venta.id}`)}>
                          NV {caso.reposicion_nota_venta.numero_lgr} ({caso.reposicion_nota_venta.estado})
                        </button>
                      )}
                      {/* Sin el estado escalar entre paréntesis: como estado de
                          despacho mentiría (regla estado_vigente) — el detalle
                          de cada documento muestra el real. */}
                      {caso.reposicion_nota_envio && (
                        <button style={st.linkDoc} onClick={() => navigate(`/notas-envio/${caso.reposicion_nota_envio.id}`)}>
                          NE {caso.reposicion_nota_envio.numero_ne} →
                        </button>
                      )}
                      {caso.reposicion_remito && (
                        <button style={st.linkDoc} onClick={() => navigate(`/remitos/${caso.reposicion_remito.id}`)}>
                          Remito {caso.reposicion_remito.numero_presea} →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Columna lateral: entrega, viajes y traza ─────────────────── */}
        <div style={st.asideCol}>
          <div style={sx.card}>
            <h3 style={{ ...sx.cardTitle, marginBottom: space.lg }}>Entrega</h3>
            <dl style={st.dl}>
              <dt style={st.dt}>Zona</dt>
              <dd style={st.dd}>{ne.zona?.nombre || <span style={sx.placeholder}>Sin zona</span>}</dd>
              <dt style={st.dt}>Fecha estimada</dt>
              <dd style={{ ...st.dd, ...sx.mono }}>{ne.fecha_entrega_est ? fmtFecha(ne.fecha_entrega_est) : <span style={sx.placeholder}>—</span>}</dd>
              {ne.instrucciones_cli && (<>
                <dt style={st.dt}>🛎️ Cliente</dt>
                <dd style={{ ...st.dd, color: '#0f766e', fontWeight: 600 }}>{ne.instrucciones_cli}</dd>
              </>)}
              <dt style={st.dt}>Horario</dt>
              <dd style={{ ...st.dd, ...sx.mono }}>
                {hhmm(ne.hora_desde) && hhmm(ne.hora_hasta)
                  ? `${hhmm(ne.hora_desde)} – ${hhmm(ne.hora_hasta)}`
                  : (hhmm(ne.hora_desde) || hhmm(ne.hora_hasta) || <span style={sx.placeholder}>—</span>)}
              </dd>
            </dl>

            {/* Viajes: cada vez que esta NE subió a una salida, con su resultado.
                El dato viene del backend — acá no se deriva nada. */}
            <div style={{ borderTop: `1px dashed ${colors.border}`, marginTop: space.lg, paddingTop: space.lg }}>
              <h3 style={{ ...sx.cardTitle, marginBottom: space.md }}>Viajes</h3>
              {viajes.length === 0 && (
                <p style={{ fontSize: text.body, color: colors.textSec, margin: 0 }}>
                  Todavía no subió a ninguna salida.
                </p>
              )}
              {viajes.map(sd => {
                const r = resultadoViaje(sd)
                return (
                  <div key={sd.id} style={st.viajeFila}>
                    <button style={st.linkDoc} onClick={() => navigate(`/salidas/${sd.salida_id}`)} title="Ver la salida">
                      Salida #{sd.salida_id}
                    </button>
                    <span style={{ fontSize: text.sec, color: colors.textSub, flex: 1 }}>
                      {sd.salida?.fletero?.nombre || '—'}
                    </span>
                    <span style={{ ...sx.badge, background: r.bg, color: r.color }}>{r.label}</span>
                  </div>
                )
              })}
              {viajes.some(sd => sd.entregado_at) && (
                <p style={{ fontSize: text.sec, color: colors.textMeta, margin: `${space.sm}px 0 0` }}>
                  Último resultado: <span style={sx.mono}>{fmtFechaHora(viajes.find(sd => sd.entregado_at)?.entregado_at)}</span>
                </p>
              )}
            </div>
          </div>

          {/* Envío Andreani (T80 en salida de Andreani): historial + "Contacté al cliente" — sugerencia #112 */}
          {ne.andreani_tracking && (
            <div style={sx.card}>
              <h3 style={{ ...sx.cardTitle, marginBottom: space.md }}>Envío Andreani</h3>
              <p style={{ fontSize: text.sec, color: colors.textSec, margin: `0 0 ${space.md}px` }}>
                Tracking <span style={{ ...sx.mono, userSelect: 'all' }}>{ne.andreani_tracking}</span>
                {ne.andreani_estado ? ` · ${ne.andreani_estado}` : ''}
                {' · '}
                <a href={`https://www.andreani.com/envio/${encodeURIComponent(ne.andreani_tracking)}`} target="_blank" rel="noreferrer" style={{ color: colors.link }}>andreani.com ↗</a>
              </p>
              <AndreaniHistorial suelto url={`/notas-envio/${ne.id}/andreani/eventos`} />
            </div>
          )}

          {/* Impresiones */}
          {ne.impresiones?.length > 0 && (
            <div style={sx.card}>
              <h3 style={{ ...sx.cardTitle, marginBottom: space.lg }}>Impresiones ({ne.impresiones.length})</h3>
              {ne.impresiones.map(i => (
                <div key={i.id} style={st.impresionFila}>
                  <span style={{ ...sx.mono, fontSize: text.sec, color: colors.textSec }}>{fmtFechaHora(i.created_at)}</span>
                  <span style={{ fontSize: text.sec, color: colors.textSub, flex: 1 }}>{i.usuario?.nombre || '—'}</span>
                  <span style={{ fontSize: text.sec, color: colors.textMeta }}>{i.tipo || ''}</span>
                </div>
              ))}
            </div>
          )}

          {/* Historial de estados (ciclo de vida — acá SÍ va el escalar) */}
          {ne.historial?.length > 0 && (
            <div style={sx.card}>
              <h3 style={{ ...sx.cardTitle, marginBottom: space.lg }}>Historial</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
                {ne.historial.map((h, i) => (
                  <div key={i} style={st.historialItem}>
                    <div style={st.historialHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: space.xs, flexWrap: 'wrap' }}>
                        {h.estado_anterior && <><ChipEscalar estado={h.estado_anterior} /><span style={{ color: colors.textPlh, fontSize: text.sec }}>→</span></>}
                        <ChipEscalar estado={h.estado_nuevo} />
                      </div>
                      <span style={{ ...sx.mono, color: colors.textSec, fontSize: text.sec }}>{fmtFechaHora(h.created_at)}</span>
                    </div>
                    {h.usuario && <div style={{ color: colors.textSub, fontSize: text.sec }}>{h.usuario.nombre}</div>}
                    {h.motivo && <div style={{ color: colors.textSec, fontSize: text.sec, fontStyle: 'italic' }}>{h.motivo}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const st = {
  wrap: { maxWidth: 1100, margin: '0 auto', fontVariantNumeric: 'tabular-nums', paddingBottom: space['3xl'] },

  breadcrumb: { display: 'flex', alignItems: 'center', gap: space.sm, fontSize: text.body, color: colors.textSec },
  breadcrumbNum: { fontFamily: font.mono, color: colors.text, fontWeight: 500, fontSize: text.body, background: colors.bgSoft, padding: '2px 8px', borderRadius: radius.xs, whiteSpace: 'nowrap' },
  toolbarAcciones: { display: 'flex', gap: space.sm, flexWrap: 'wrap' },

  successBox: { background: colors.okBg, color: colors.ok, padding: '8px 12px', borderRadius: radius.sm, marginBottom: space.lg, fontSize: text.body, fontWeight: 500 },
  errorBox: { background: colors.errBg, color: colors.err, padding: '8px 12px', borderRadius: radius.sm, marginBottom: space.lg, fontSize: text.body },

  casoBox: { background: colors.attnBg, border: `1px solid ${colors.attnBorder}`, borderRadius: radius.md, padding: space.lg, marginBottom: space.lg },
  casoTextarea: { width: '100%', minHeight: 64, marginTop: space.md, resize: 'vertical', boxSizing: 'border-box', padding: '6px 10px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit' },
  casoItem: { display: 'flex', alignItems: 'center', gap: space.md, padding: '4px 0', fontSize: text.body, cursor: 'pointer' },
  casoFila: { padding: `${space.lg}px 0`, borderBottom: `1px solid ${colors.borderSoft}`, fontSize: text.body },

  grid: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: space.xl, alignItems: 'start' },
  mainCol: { display: 'flex', flexDirection: 'column', gap: space.xl, minWidth: 0 },
  asideCol: { display: 'flex', flexDirection: 'column', gap: space.xl, minWidth: 0 },

  // El documento como ancla visual (mismo lenguaje que el detalle de remito)
  documento: { background: colors.bg, borderRadius: radius.lg, border: `1px solid ${colors.border}`, overflow: 'hidden' },
  docHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', padding: `${space['2xl']}px ${space['3xl']}px`, borderBottom: `2px solid ${colors.borderStrong}`, gap: space.lg, flexWrap: 'wrap' },
  docHeaderLeft: { display: 'flex', flexDirection: 'column', gap: space.xs },
  docLabel: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.08em' },
  docNumero: { fontFamily: font.mono, fontSize: text.display, fontWeight: 600, color: colors.text, lineHeight: 1 },
  docDestinatario: { padding: `${space.xl}px ${space['3xl']}px`, borderBottom: `1px solid ${colors.border}` },
  docDestNombre: { fontSize: text.title, fontWeight: 600, color: colors.text, marginBottom: space.xs },
  docDestDatos: { display: 'flex', flexWrap: 'wrap', gap: space.sm, alignItems: 'center', fontSize: text.body, color: colors.textSub },
  docDestNotas: { marginTop: space.md, fontSize: text.body, color: colors.warn, background: colors.warnBg, borderRadius: radius.xs, padding: '4px 10px', display: 'inline-block' },

  tr: { borderBottom: `1px solid ${colors.borderSoft}` },
  td: { padding: `${space.lg}px ${space.xl}px`, verticalAlign: 'middle', fontSize: text.body, color: colors.text },

  dl: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: `${space.md}px ${space.xl}px`, alignItems: 'baseline', margin: 0 },
  dt: { fontWeight: 600, fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap' },
  dd: { margin: 0, fontSize: text.body, color: colors.text },

  linkDoc: { background: 'none', border: 'none', padding: 0, color: colors.link, fontWeight: 500, cursor: 'pointer', fontFamily: font.mono, fontSize: text.body, textDecoration: 'underline' },

  // Tabla "papel" del documento — mismo lenguaje que el detalle del remito.
  docDetalle: { padding: `${space.xl}px ${space['3xl']}px ${space['2xl']}px` },
  docTable: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  docTh: { padding: '8px 8px', textAlign: 'left', borderBottom: `1px solid ${colors.borderStrong}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  docTr: { borderBottom: `1px solid ${colors.borderSoft}` },
  docTd: { padding: '8px 8px', verticalAlign: 'middle', color: colors.text, fontSize: text.body },
  docTdCodigo: { padding: '8px 8px', verticalAlign: 'middle', fontFamily: font.mono, fontSize: text.body, fontWeight: 500, color: colors.text },

  viajeFila: { display: 'flex', alignItems: 'center', gap: space.md, padding: `${space.sm}px 0`, borderBottom: `1px solid ${colors.borderSoft}`, marginBottom: space.sm },
  impresionFila: { display: 'flex', alignItems: 'center', gap: space.md, padding: `${space.xs}px 0`, borderBottom: `1px solid ${colors.borderSoft}` },

  historialItem: { background: colors.bgSoft, borderRadius: radius.sm, padding: `${space.md}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.xs },
  historialHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md },
}
